import React, { useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  ArrowLeft,
  Check,
  MessageSquareText,
  Star,
  Store,
} from "lucide-react";
import { AppDispatch, RootState } from "../../../../../store";
import { createStoreReview } from "../../../../../Redux/storeReview/slice";
import "./FoodSellerFeeback.css";

const FoodSellerFeeback = () => {
  const { sellerId, id } = useParams<{ sellerId?: string; id?: string }>();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId") || "";
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const { loading } = useSelector((state: RootState) => state.storeReview);

  const [rating, setRating] = useState<number>(0);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [review, setReview] = useState<string>("");
  const [isItemAsDescribed, setIsItemAsDescribed] = useState<string>("");

  const targetSellerId = sellerId || id;
  const activeRating = hoverRating || rating;

  const goBack = () => navigate("/FoodOrderList");

  const handleSubmit = async () => {
    if (!targetSellerId) {
      toast.error("Seller ID is missing from URL.");
      return;
    }
    if (!orderId) {
      toast.error("Order ID is missing from URL.");
      return;
    }
    if (rating === 0) {
      toast.error("Please provide a rating.");
      return;
    }
    if (!review.trim()) {
      toast.error("Please provide comments about your experience.");
      return;
    }

    let finalReview = review;
    if (isItemAsDescribed) {
      finalReview = `Item as described: ${isItemAsDescribed}\n\n${review}`;
    }

    try {
      const resultAction = await dispatch(
        createStoreReview({
          targetSellerId,
          orderId,
          rating,
          review: finalReview,
        }),
      );

      if (createStoreReview.fulfilled.match(resultAction)) {
        toast.success("Feedback submitted successfully!");
        setRating(0);
        setReview("");
        setIsItemAsDescribed("");
        navigate(-1);
      } else {
        toast.error(
          (resultAction.payload as string) || "Failed to submit feedback",
        );
      }
    } catch (error: any) {
      toast.error(error.message || "An error occurred");
    }
  };

  return (
    <section className="food-feedback-page">
      <div className="food-feedback-shell">
        <button
          type="button"
          className="food-feedback-back"
          onClick={goBack}
        >
          <ArrowLeft size={18} strokeWidth={2.1} />
          <span>Back to Food Orders</span>
        </button>

        <header className="food-feedback-header">
          <div className="food-feedback-heading">
            <span className="food-feedback-heading-icon">
              <Store size={22} strokeWidth={2} />
            </span>
            <div>
              <span className="food-feedback-kicker">FOOD & DINING</span>
              <h1>Restaurant Feedback</h1>
              <p>
                Rate the restaurant and tell us how well your order matched
                the listing.
              </p>
            </div>
          </div>
          {targetSellerId && (
            <span className="food-feedback-order-chip">
              SELLER #{targetSellerId}
            </span>
          )}
        </header>

        <div className="food-feedback-layout">
          <aside className="food-feedback-summary-card">
            <div className="food-feedback-restaurant">
              <div className="food-feedback-image">
                <img
                  src="../../assets/img/food/food-1.jpg"
                  alt="Restaurant"
                  loading="lazy"
                />
              </div>
              <div className="food-feedback-restaurant-copy">
                <span>RESTAURANT</span>
                <h2>Restaurant</h2>
                <p>Share your dining experience</p>
              </div>
            </div>
            <div className="food-feedback-summary-divider" />
            <div className="food-feedback-meta-grid">
              <div className="food-feedback-meta-card">
                <span className="food-feedback-meta-icon">
                  <MessageSquareText size={17} strokeWidth={2} />
                </span>
                <div>
                  <span>SELLER ID</span>
                  <strong>#{targetSellerId || "N/A"}</strong>
                </div>
              </div>
            </div>
          </aside>

          <form
            className="food-feedback-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <section className="food-feedback-section">
              <div className="food-feedback-section-head">
                <div>
                  <span className="food-feedback-section-label">
                    RESTAURANT RATING
                  </span>
                  <h2>Rate Restaurant</h2>
                  <p>Select a rating from 1 to 5 stars.</p>
                </div>
                <span className="food-feedback-step">01</span>
              </div>

              <div
                className="food-feedback-stars"
                onMouseLeave={() => setHoverRating(0)}
              >
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    type="button"
                    key={value}
                    className={value <= activeRating ? "active" : ""}
                    onMouseEnter={() => setHoverRating(value)}
                    onClick={() => setRating(value)}
                    aria-label={`Rate restaurant ${value} star`}
                    aria-pressed={rating === value}
                  >
                    <Star size={31} strokeWidth={1.8} />
                  </button>
                ))}
              </div>
            </section>

            <section className="food-feedback-section">
              <div className="food-feedback-section-head">
                <div>
                  <span className="food-feedback-section-label">
                    ORDER ACCURACY
                  </span>
                  <h2>Item as described by the restaurant?</h2>
                  <p>
                    Tell us whether the food matched what the restaurant
                    showed.
                  </p>
                </div>
                <span className="food-feedback-step">02</span>
              </div>

              <div className="food-feedback-choice-row">
                {["Yes", "No"].map((option) => (
                  <button
                    type="button"
                    key={option}
                    className={
                      isItemAsDescribed === option ? "selected" : ""
                    }
                    onClick={() => setIsItemAsDescribed(option)}
                  >
                    <span>
                      {isItemAsDescribed === option && (
                        <Check size={14} strokeWidth={3} />
                      )}
                    </span>
                    {option}
                  </button>
                ))}
              </div>
            </section>

            <section className="food-feedback-section">
              <div className="food-feedback-section-head">
                <div>
                  <span className="food-feedback-section-label">
                    COMMENTS
                  </span>
                  <h2>Share your experience</h2>
                  <p>
                    Add comments about your experience with this restaurant.
                  </p>
                </div>
                <span className="food-feedback-step">03</span>
              </div>

              <textarea
                value={review}
                onChange={(e) => setReview(e.target.value)}
                maxLength={500}
                placeholder="Please enter comments here about your experience with this restaurant."
                aria-label="Restaurant feedback comments"
              />
              <div className="food-feedback-character-count">
                {review.length} / 500
              </div>
            </section>

            <footer className="food-feedback-form-footer">
              <button
                type="button"
                className="secondary"
                onClick={goBack}
                disabled={loading}
              >
                Cancel
              </button>
              <button type="submit" className="primary" disabled={loading}>
                {loading ? "Submitting..." : "Submit Feedback"}
              </button>
            </footer>
          </form>
        </div>
      </div>
    </section>
  );
};

export default FoodSellerFeeback;
